import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import {
    fetchAllUserSubscriptions,
    createSubscription,
    deleteSubscription,
} from '@/services/subscriptionService'
import type { SubscriptionItem, FetchUserSubscriptionsResult } from '@/services/subscriptionService'
import { subscriberCountQueryKey } from '@/hooks/useSubscriberCount'

export interface UseSubscriptionToggleResult {
    isSubscribed: boolean
    isLoading: boolean
    isStatusLoading: boolean
    handleToggle: () => void
}

/**
 * Manages subscribe/unsubscribe for a specific publication by its AT-URI.
 *
 * Checks subscription status at the publication URI level (not author DID),
 * so users with multiple publications show correct per-publication state.
 *
 * Uses two cache keys:
 *   ['subscriptionRecords', did]     — flat useQuery for status checks and count
 *   ['subscriptionRecordsList', did] — infinite useQuery for the paginated list UI
 */
export function useSubscriptionToggle(
    publicationUri: string | undefined,
    authorDid: string | undefined
): UseSubscriptionToggleResult {
    const { session } = useAuth()
    const queryClient = useQueryClient()

    const recordsQueryKey = ['subscriptionRecords', session?.did] as const
    const listQueryKey = ['subscriptionRecordsList', session?.did] as const

    const { data: recordsData, isLoading: isStatusLoading } = useQuery({
        queryKey: recordsQueryKey,
        queryFn: () => fetchAllUserSubscriptions(session!),
        enabled: !!session && !!publicationUri,
        staleTime: 10 * 60 * 1000,
    })

    const records = recordsData?.subscriptions
    // All records matching this publication, not just the first — duplicate
    // subscription records can exist on the PDS (mackinac#2603), and
    // unsubscribe must delete every one of them or a surviving duplicate
    // keeps the publication live in the articles feed permanently.
    const matchingRecords = publicationUri
        ? (records ?? []).filter(r => r.publicationUri === publicationUri)
        : []
    const isSubscribed = matchingRecords.length > 0

    const toggleMutation = useMutation({
        mutationFn: async ({ wasSubscribed }: { wasSubscribed: boolean }) => {
            if (!session || !publicationUri || !authorDid) {
                throw new Error('Not authenticated or missing publication details')
            }
            if (wasSubscribed) {
                if (matchingRecords.length === 0) throw new Error('Subscription record not found')
                // allSettled, not all: a partial failure (some duplicates deleted,
                // others not) shouldn't reject the whole mutation and trigger
                // onError's rollback to "still subscribed" — the deletes that did
                // succeed are already gone from the PDS. Only surface an error
                // when every delete failed; onSettled's invalidation reconciles
                // the exact remaining state either way.
                const results = await Promise.allSettled(
                    matchingRecords.map(r => deleteSubscription(session, r.rkey))
                )
                const allFailed = results.every(r => r.status === 'rejected')
                if (allFailed) {
                    throw (results[0] as PromiseRejectedResult).reason
                }
                return null
            }
            return await createSubscription(session, publicationUri)
        },
        onMutate: ({ wasSubscribed }) => {
            void queryClient.cancelQueries({ queryKey: recordsQueryKey })
            void queryClient.cancelQueries({ queryKey: listQueryKey })

            const previousRecords =
                queryClient.getQueryData<FetchUserSubscriptionsResult>(recordsQueryKey)
            const previousList =
                queryClient.getQueryData<InfiniteData<FetchUserSubscriptionsResult>>(listQueryKey)

            queryClient.setQueryData<FetchUserSubscriptionsResult>(recordsQueryKey, old => {
                const current = old?.subscriptions ?? []
                if (wasSubscribed) {
                    return {
                        ...old,
                        subscriptions: current.filter(r => r.publicationUri !== publicationUri),
                    }
                }
                return {
                    subscriptions: [
                        ...current,
                        { uri: '', rkey: '', publicationUri: publicationUri! },
                    ],
                }
            })

            queryClient.setQueryData<InfiniteData<FetchUserSubscriptionsResult>>(
                listQueryKey,
                old => {
                    if (!old) return old
                    if (wasSubscribed) {
                        return {
                            ...old,
                            pages: old.pages.map(page => ({
                                ...page,
                                subscriptions: page.subscriptions.filter(
                                    r => r.publicationUri !== publicationUri
                                ),
                            })),
                        }
                    }
                    const [firstPage, ...rest] = old.pages
                    const optimisticItem: SubscriptionItem = {
                        uri: '',
                        rkey: '',
                        publicationUri: publicationUri!,
                    }
                    return {
                        ...old,
                        pages: [
                            {
                                ...firstPage,
                                subscriptions: [optimisticItem, ...firstPage.subscriptions],
                            },
                            ...rest,
                        ],
                    }
                }
            )

            return { previousRecords, previousList }
        },
        onSuccess: (data, { wasSubscribed }) => {
            // This records query is Mongo-backed (populated by the firehose
            // consumer), which can briefly lag a just-completed PDS write —
            // refetching here could return stale data and clobber the optimistic
            // update (causing the button to flip back). Instead, reconcile both
            // caches directly with the authoritative result from the PDS write.
            // (The onSettled handler below still catches up via an invalidation
            // if this reconciliation itself was incomplete.)
            if (!wasSubscribed && data) {
                queryClient.setQueryData<FetchUserSubscriptionsResult>(recordsQueryKey, old => {
                    const current = old?.subscriptions ?? []
                    const withoutPlaceholder = current.filter(
                        r => !(r.publicationUri === publicationUri && r.rkey === '')
                    )
                    return {
                        ...old,
                        subscriptions: [
                            ...withoutPlaceholder,
                            { uri: data.uri, rkey: data.rkey, publicationUri: publicationUri! },
                        ],
                    }
                })
                queryClient.setQueryData<InfiniteData<FetchUserSubscriptionsResult>>(
                    listQueryKey,
                    old => {
                        if (!old) return old
                        return {
                            ...old,
                            pages: old.pages.map((page, i) => {
                                const withoutPlaceholder = page.subscriptions.filter(
                                    r => !(r.publicationUri === publicationUri && r.rkey === '')
                                )
                                return {
                                    ...page,
                                    subscriptions:
                                        i === 0
                                            ? [
                                                  {
                                                      uri: data.uri,
                                                      rkey: data.rkey,
                                                      publicationUri: publicationUri!,
                                                  },
                                                  ...withoutPlaceholder,
                                              ]
                                            : withoutPlaceholder,
                                }
                            }),
                        }
                    }
                )
            }
            // Mark the articles feed query stale without refetching now — getArticlesFeed
            // reads the viewer's subscriptions live from the PDS, but caches the built
            // article list server-side in Redis for 300s (#2605, tracked separately and
            // out of scope here). An immediate refetch would very likely just re-hit that
            // cache and re-freshen the same stale list for another staleTime (10 min).
            // Deferring lets it refetch the next time it mounts, by which point the
            // server cache has a much better chance of having rolled over. Scoped to this
            // user's DID (omitting the trailing `source` key segment) so all source
            // variants ('all' | 'standard' | 'rss') are covered.
            void queryClient.invalidateQueries({
                queryKey: ['articlesFeed', 'pages', session?.did],
                refetchType: 'none',
            })
            void queryClient.setQueryData<number | null>(
                subscriberCountQueryKey(publicationUri!),
                old => {
                    if (typeof old !== 'number') return old
                    return wasSubscribed ? Math.max(0, old - 1) : old + 1
                }
            )
        },
        onError: (_err, _vars, context) => {
            if (context?.previousRecords !== undefined) {
                queryClient.setQueryData<FetchUserSubscriptionsResult>(
                    recordsQueryKey,
                    context.previousRecords
                )
            }
            if (context?.previousList !== undefined) {
                queryClient.setQueryData<InfiniteData<FetchUserSubscriptionsResult>>(
                    listQueryKey,
                    context.previousList
                )
            }
        },
        onSettled: () => {
            // Self-correction net for #2603: if a multi-record delete partially
            // fails (some but not all duplicates removed) or any other partial
            // failure leaves the cache disagreeing with the PDS, mark this
            // stale so the next read reflects the PDS's actual state instead of
            // the optimistic/rolled-back value. refetchType: 'none' avoids an
            // immediate refetch, since it could race the same delay this
            // query's own fetch can have relative to a just-completed write.
            void queryClient.invalidateQueries({ queryKey: recordsQueryKey, refetchType: 'none' })
        },
    })

    return {
        isSubscribed,
        isLoading: isStatusLoading || toggleMutation.isPending,
        isStatusLoading,
        handleToggle: () => {
            // Guard synchronously rather than relying on a `disabled` prop, which
            // only takes effect after the re-render caused by onMutate's optimistic
            // flip — onMutate awaits cancelQueries first, leaving a window where a
            // rapid double-click can land before isPending flips and double-write a
            // subscription record (mackinac#2603). Mirrors mobile's same guard.
            if (toggleMutation.isPending) return
            toggleMutation.mutate({ wasSubscribed: isSubscribed })
        },
    }
}
